import type { Campaign, CampaignAudienceSegment, CampaignPlan, Creative } from '@/api/types';
import type { CampaignUpdateInput } from '@/api/manage-types';
import type { MetaMediaValidationReport } from '@/lib/media-validation/meta-validator';
import {
  libraryToAssets,
  type CtvLibraryAssets,
  type DspLibraryAssets,
  type LibraryChannel,
  type LibraryCreative,
  type MetaLibraryAssets,
} from '@/lib/creative-library-assets';
import type {
  CreativeBuilderProduction,
  CreativeSlot,
  CtvSlotData,
  DspSlotData,
  MetaAssetsBySlot,
  MetaCreativeSlot,
} from '@/lib/creative-required-issues';
import {
  CHANNEL_FORMATS,
  metaDefaults,
  metaFieldsFromCreative,
  metaCopyIssues,
  serializeMetaCreativeSpec,
  withoutStoredCtaRefusal,
  type CreativeCopySource,
  type CreativeFieldValues,
} from '@/screens/creative-config';

/** One creative a plan pins, at the revision it flies. */
type PlanCreativePin = CampaignPlan['creatives'][number];

/** What slot seeding reads from a plan (`CampaignPlan` satisfies it). */
export interface CreativePinPlan {
  creatives?: readonly Pick<PlanCreativePin, 'creative_id' | 'creative_revision'>[];
}

/** A list creative whose copy a pinned slot is seeded from (`Creative` satisfies it). */
export type PinnedCopyCreative = Pick<Creative, 'id' | 'name' | 'revision'> & CreativeCopySource;

/** A list creative whose stored assets a pinned slot previews (`Creative` satisfies it). */
export type PinnedLibraryCreative = LibraryCreative & Pick<Creative, 'revision'>;

/** One creative slot seeded from a saved plan. */
export interface PlanCreativeSlot extends MetaCreativeSlot {
  metaFields: CreativeFieldValues;
}

export interface PlanCreativeSlots {
  slots: PlanCreativeSlot[];
  /** 1-based positions of the pins that did not resolve. */
  unresolvedPositions: number[];
}

/** What `planMatchesSavedFields` compares on the server's plan (`CampaignPlan` satisfies it). */
export interface SavedPlanSnapshot {
  campaign: Pick<
    Campaign,
    'name' | 'production_id' | 'channel' | 'budget_cents' | 'start_date' | 'end_date' | 'markup_on' | 'starred'
  > & {
    flights?: readonly Pick<Campaign['flights'][number], 'id' | 'name' | 'start_date' | 'end_date' | 'budget_cents'>[];
  };
  creatives?: readonly Pick<PlanCreativePin, 'creative_id'>[];
  audience_segments?: readonly Pick<CampaignAudienceSegment, 'audience_id' | 'allocation_bps'>[];
}

/** The plan fields the wizard sends on a create or an update. */
export type CampaignPlanFields = Pick<
  CampaignUpdateInput,
  | 'name'
  | 'production_id'
  | 'channel'
  | 'audience_segments'
  | 'creative_ids'
  | 'budget_cents'
  | 'start_date'
  | 'end_date'
  | 'flights'
  | 'markup_on'
  | 'starred'
>;

/** A slot asset a draft uploads: picked in this browser, so it always carries its file.
 *  A Meta or CTV `CreativeSlotAsset` and a Display `DspBanner` both satisfy it once their
 *  `file` is known to be there. */
export interface CreativeDraftAsset {
  file: File;
  type: 'image' | 'video';
  name: string;
  url: string;
  width?: number | null;
  height?: number | null;
  w?: number | null;
  h?: number | null;
  durationMs?: number | null;
  duration?: number | null;
  validation?: MetaMediaValidationReport;
}

export interface CreativeDraftPlacement {
  placementTag: string;
  asset: CreativeDraftAsset;
}

/** One creative the wizard will create from local files. */
export interface CreativeDraft {
  /** The slot id the draft was built from. */
  key: string;
  name: string;
  mediaType: 'image' | 'video';
  format: string;
  spec: string;
  destinationUrl: string;
  placements: CreativeDraftPlacement[];
}

export interface CampaignCreativeSelections {
  /** Library creatives referenced by id; the wizard never writes them. */
  libraryIds: string[];
  drafts: CreativeDraft[];
}

/** The fields `localAssetFingerprint` reads. */
export interface FingerprintedAsset {
  file?: File | null;
  width?: number | null;
  height?: number | null;
  w?: number | null;
  h?: number | null;
  durationMs?: number | null;
}

/** A slot asset picked in this browser. Only those become a draft's placements. */
function hasLocalFile<T extends { file?: File | null }>(
  asset: T | null | undefined,
): asset is T & { file: File } {
  return !!asset?.file;
}

export function campaignIdForCreate() {
  return `campaign_${crypto.randomUUID().replaceAll('-', '')}`.slice(0, 64);
}

export function planChangedElsewhereError(revision: number) {
  const error = new Error(
    `This campaign plan changed outside this tab — it is now at revision ${revision}. `
    + 'Reload the page to review the latest saved plan before you launch it.',
  );
  // Named so its telemetry line says which conclusion we reached, not just
  // "Error". This one always pairs with the transport's own 409 line: two
  // different facts — what the server said, and what we decided it meant.
  error.name = 'PlanChangedElsewhereError';
  return error;
}

/**
 * `plan`, when it is the revision this wizard's own write produced (`written`: the
 * `expected_revision` a PUT sent plus one, or 1 for a create), and otherwise the
 * changed-elsewhere refusal.
 *
 * The API reads the plan back after its commit, so an edit that lands between the two (an API
 * key's, say) comes back in the answer to our save. Adopting it would hand the launch a
 * revision whose contents the form never showed: the form still holds what we sent. So the
 * only revision a person has reviewed is the one they wrote, and anything else stops the
 * wizard until they reload (docs/plans/claude-connector.md, Astra review of commit 5).
 */
export function ownWriteOrChangedElsewhere<P extends { revision: number }>(plan: P, written: number): P {
  if (plan.revision !== written) throw planChangedElsewhereError(plan.revision);
  return plan;
}

/** Seeds one slot per pinned creative, and reports the pins that would not
 *  resolve.
 *
 *  A miss used to be silent: `metaFieldsFromCreative(undefined)` is `{}`, the
 *  merge in `campaignCreativeSelections` filled the gap from `metaDefaults`, and
 *  the operator saw seeded values where a real creative's copy belonged. A pin
 *  misses when the creative falls outside the list route's `LIMIT 500`, outside
 *  the caller's production scope, or — for the copy specifically — when its spec
 *  is a legacy non-JSON string. `unresolvedPositions` is the same contract
 *  `audienceSlotsFromPlan` returns, and the save refuses while it is non-empty. */
export function creativeSlotsFromPlan(
  plan: CreativePinPlan | null | undefined,
  prefix: string,
  label: string,
  availableCreatives: readonly PinnedCopyCreative[] = [],
): PlanCreativeSlots {
  const pins = plan?.creatives ?? [];
  if (!pins.length) {
    return { slots: [{ id: `${prefix}-1`, label: `${label} 1`, metaFields: {} }], unresolvedPositions: [] };
  }
  const unresolvedPositions: number[] = [];
  const slots = pins.map((pin, index): PlanCreativeSlot => {
    const id = pin.creative_id;
    const creative = availableCreatives.find((candidate) =>
      candidate.id === id && candidate.revision === pin.creative_revision);
    // Readability only. A stored CTA Meta refuses ("Book Now") is copy the pin
    // does carry, so it is not "unreadable": the creatives step and the
    // `meta_creative_cta` preflight refuse it with the API's own reason, and
    // calling it unreadable here would ask for a reload that never clears it.
    const fields = metaFieldsFromCreative(creative);
    const unresolved = !creative || (creative.channel === 'meta'
      && withoutStoredCtaRefusal(metaCopyIssues(fields), fields).length > 0);
    if (unresolved) unresolvedPositions.push(index + 1);
    const name = creative?.name || id;
    return {
      id: `${prefix}-saved-${index + 1}`,
      label: `${label} ${index + 1}`,
      adName: name,
      metaFields: fields,
      unresolved,
      // The plan pins a creative REVISION; the slot names it.
      fromLibrary: { id, name, revision: pin.creative_revision },
    };
  });
  return { slots, unresolvedPositions };
}

export function creativeDataFromSlots(
  slots: readonly CreativeSlot[],
  availableCreatives: readonly PinnedLibraryCreative[],
  production: CreativeBuilderProduction | null | undefined,
  channel: 'meta',
): Record<string, MetaLibraryAssets>;
export function creativeDataFromSlots(
  slots: readonly CreativeSlot[],
  availableCreatives: readonly PinnedLibraryCreative[],
  production: CreativeBuilderProduction | null | undefined,
  channel: 'ctv',
): Record<string, CtvLibraryAssets>;
export function creativeDataFromSlots(
  slots: readonly CreativeSlot[],
  availableCreatives: readonly PinnedLibraryCreative[],
  production: CreativeBuilderProduction | null | undefined,
  channel: 'dsp',
): Record<string, DspLibraryAssets>;
export function creativeDataFromSlots(
  slots: readonly CreativeSlot[],
  availableCreatives: readonly PinnedLibraryCreative[],
  production: CreativeBuilderProduction | null | undefined,
  channel: LibraryChannel,
): Record<string, MetaLibraryAssets | CtvLibraryAssets | DspLibraryAssets> {
  return Object.fromEntries(slots.map((slot) => {
    const creative = availableCreatives.find(
      (candidate) => candidate.id === slot.fromLibrary?.id
        && candidate.revision === slot.fromLibrary?.revision,
    );
    const fallback: MetaLibraryAssets | CtvLibraryAssets | DspLibraryAssets = channel === 'meta'
      ? {}
      : channel === 'ctv'
        ? { destUrl: '', asset: null }
        : { destUrl: '', banners: [] };
    return [
      slot.id,
      creative ? libraryToAssets(creative, production, channel) : fallback,
    ];
  }));
}

export function creativeIdForCreate() {
  return `creative_${crypto.randomUUID().replaceAll('-', '')}`.slice(0, 64);
}

export function localAssetFingerprint(asset: FingerprintedAsset | null | undefined) {
  const file = asset?.file;
  return file
    ? `${file.name}:${file.size}:${file.lastModified}:${asset.width ?? asset.w}x${asset.height ?? asset.h}:${asset.durationMs ?? ''}`
    : null;
}

// A PUT can commit server-side and still fail client-side (the response is
// lost after D1 commits the batch). The wizard then holds a stale
// expected_revision and every save 409s. Deciding whether the server's
// current plan IS the plan we just tried to write is what lets the wizard
// adopt the committed revision instead of dead-ending on the conflict.
export function planMatchesSavedFields(
  plan: SavedPlanSnapshot | null | undefined,
  fields: CampaignPlanFields | null | undefined,
) {
  if (!plan?.campaign || !fields) return false;
  const campaign = plan.campaign;
  if (
    campaign.name !== fields.name
    || campaign.production_id !== fields.production_id
    || campaign.channel !== fields.channel
    || campaign.budget_cents !== fields.budget_cents
    || campaign.start_date !== fields.start_date
    || campaign.end_date !== fields.end_date
    || campaign.markup_on !== fields.markup_on
    || campaign.starred !== fields.starred
  ) return false;

  const savedFlights = campaign.flights ?? [];
  if (savedFlights.length !== fields.flights.length) return false;
  const flightsById = new Map(savedFlights.map((flight) => [flight.id, flight]));
  for (const sent of fields.flights) {
    const saved = flightsById.get(sent.id);
    if (
      !saved
      || saved.name !== sent.name
      || saved.start_date !== sent.start_date
      || saved.end_date !== sent.end_date
      || saved.budget_cents !== sent.budget_cents
    ) return false;
  }

  const savedCreativeIds = new Set((plan.creatives ?? []).map((creative) => creative.creative_id));
  const sentCreativeIds = new Set(fields.creative_ids);
  if (savedCreativeIds.size !== sentCreativeIds.size) return false;
  for (const id of sentCreativeIds) {
    if (!savedCreativeIds.has(id)) return false;
  }

  const savedSegments = plan.audience_segments ?? [];
  if (savedSegments.length !== fields.audience_segments.length) return false;
  return savedSegments.every((segment, index) =>
    segment.audience_id === fields.audience_segments[index]?.audience_id
    && segment.allocation_bps === fields.audience_segments[index]?.allocation_bps);
}

// The durable record of the Preview & Approve step is the creatives' own
// status: saving with the step confirmed submits and approves every draft
// creative, and the backend preflight gates launch on exactly
// `status === 'approved'`. Reading it back is what lets a reloaded wizard
// keep the step green instead of forgetting the review ever happened.
export function planCreativesAllApproved(
  plan: Pick<SavedPlanSnapshot, 'creatives'> | null | undefined,
  availableCreatives: readonly Pick<Creative, 'id' | 'status'>[] = [],
) {
  const ids = plan?.creatives?.map((creative) => creative.creative_id) ?? [];
  if (!ids.length) return false;
  return ids.every((id) =>
    availableCreatives.find((creative) => creative.id === id)?.status === 'approved');
}

export function campaignCreativeSelections(
  channel: string | null | undefined,
  campaignName: string,
  production: CreativeBuilderProduction | null | undefined,
  metaCreatives: readonly MetaCreativeSlot[],
  metaAssets: MetaAssetsBySlot | null | undefined,
  ctvCreatives: readonly CreativeSlot[],
  ctvData: Record<string, CtvSlotData> | null | undefined,
  dspGroups: readonly CreativeSlot[],
  dspData: Record<string, DspSlotData> | null | undefined,
): CampaignCreativeSelections {
  const slots: readonly MetaCreativeSlot[] = channel === 'meta'
    ? metaCreatives
    : channel === 'ctv'
      ? ctvCreatives
      : channel === 'dsp'
        ? dspGroups
        : [];

  const libraryIds: string[] = [];
  const drafts: CreativeDraft[] = [];
  slots.forEach((slot, slotIndex) => {
    const metaValues = channel === 'meta'
      ? {
          ...metaDefaults(
            production,
            'meta',
            CHANNEL_FORMATS.meta.name,
            slotIndex === 0 ? '' : slot.label,
          ),
          ...(slot.metaFields || {}),
        }
      : null;
    // `metaValues` is set exactly when the channel is Meta.
    const destinationUrl = metaValues
      ? metaValues.dest_url
      : channel === 'ctv'
        ? ctvData?.[slot.id]?.destUrl
        : dspData?.[slot.id]?.destUrl;
    const ctvAsset = ctvData?.[slot.id]?.asset;
    const placements: CreativeDraftPlacement[] = channel === 'meta'
      ? Object.entries(metaAssets?.[slot.id] || {})
          .flatMap(([key, asset]) => hasLocalFile(asset) ? [{ placementTag: `meta_${key}`, asset }] : [])
      : channel === 'ctv'
        ? (hasLocalFile(ctvAsset)
            ? [{ placementTag: 'ctv_16x9', asset: ctvAsset }]
            : [])
        : (dspData?.[slot.id]?.banners || [])
            .filter(hasLocalFile)
            .map((asset, index) => ({
              placementTag: `dsp_${asset.width ?? asset.w}x${asset.height ?? asset.h}_${index + 1}`,
              asset,
            }));

    const firstPlacement = placements[0];
    if (!firstPlacement) {
      if (slot.fromLibrary?.id) libraryIds.push(slot.fromLibrary.id);
      return;
    }

    const first = firstPlacement.asset;
    const format = channel === 'meta'
      ? placements.map((placement) => placement.placementTag.replace('meta_', '')).join(' + ')
      : channel === 'ctv'
        ? `${Math.round((first.durationMs ?? 0) / 1000) || first.duration || 0}s · 16:9`
        : placements.map((placement) =>
            `${placement.asset.width ?? placement.asset.w}×${placement.asset.height ?? placement.asset.h}`).join(' + ');
    drafts.push({
      key: slot.id,
      name: slot.adName || `${campaignName || 'Campaign'} · Creative ${slotIndex + 1}`,
      mediaType: first.type === 'video' ? 'video' : 'image',
      format,
      spec: metaValues
        ? serializeMetaCreativeSpec(metaValues)
        : `${first.width ?? first.w}×${first.height ?? first.h}`,
      destinationUrl: destinationUrl?.trim() || '',
      placements,
    });
  });

  return { libraryIds: [...new Set(libraryIds)], drafts };
}
